import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Combobox } from '@/components/Combobox'
import { ConfirmActionDialog } from '@/components/ConfirmActionDialog'
import { PageHeader } from '@/components/page'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  createFormDraft,
  describeProblem,
  evaluateOnServer,
  evaluationDrift,
  formsListQuery,
  getFormVersion,
  publishFormVersion,
  replaceFormDraft,
  type ServerEvaluation,
  type ServerForm,
} from '@/forms/api'
import { FormRenderer } from '@/forms/renderer/FormRenderer'
import { pickText } from '@/forms/renderer/helpers'
import { ResultPanel } from '@/forms/renderer/ResultPanel'
import type { Answers, EvaluationResult, FormDefinition } from '@/forms/types'
import { canonicalJson } from '@/forms/canonical'
import type { validateDefinition as ValidateFn } from '@/forms/validate'
import { requireNav } from '@/nav/guard'

export const Route = createFileRoute('/_app/admin/forms/')({
  beforeLoad: requireNav('forms'),
  component: FormsAdminPage,
})

/** Fallback, solange das Backend keine Formularliste liefert: Referenzformulare des Startup-Imports. */
const FALLBACK = [
  { key: 'tiefbau', version: 1 },
  { key: 'belag', version: 1 },
] as const

const pretty = (value: unknown) => JSON.stringify(value, null, 2)

function FormsAdminPage() {
  const { t, i18n } = useTranslation()
  const lang = i18n.resolvedLanguage ?? 'en'
  const ids = { key: useId(), version: useId(), editor: useId(), summary: useId() }

  const [keyInput, setKeyInput] = useState('tiefbau')
  const [versionInput, setVersionInput] = useState('1')
  const [loaded, setLoaded] = useState<ServerForm | null>(null)
  const [text, setText] = useState('')
  const queryClient = useQueryClient()
  const forms = useQuery(formsListQuery)
  const known: { key: string; version: number }[] = forms.data ?? [...FALLBACK]
  const [notice, setNotice] = useState<string | null>(null)
  const [answers, setAnswers] = useState<Answers>({})
  const [localResult, setLocalResult] = useState<EvaluationResult | null>(null)
  const [serverResult, setServerResult] = useState<ServerEvaluation | null>(null)
  const [publishOpen, setPublishOpen] = useState(false)

  const parsed = useMemo(() => {
    if (text.trim() === '') return null
    try {
      return { ok: true as const, value: JSON.parse(text) as Record<string, unknown> }
    } catch (error) {
      return { ok: false as const, message: error instanceof Error ? error.message : String(error) }
    }
  }, [text])
  // Ajv (Schemaprüfung) ist groß: erst beim Öffnen der Verwaltung nachladen
  const [validateDefinition, setValidate] = useState<typeof ValidateFn | null>(null)
  useEffect(() => {
    void import('@/forms/validate').then((m) => setValidate(() => m.validateDefinition))
  }, [])
  const schemaCheck = useMemo(
    () => (parsed?.ok && validateDefinition ? validateDefinition(parsed.value) : null),
    [parsed, validateDefinition],
  )
  const definition =
    parsed?.ok && schemaCheck?.valid ? (parsed.value as unknown as FormDefinition) : null

  const readOnly = loaded !== null && loaded.status !== 'DRAFT'
  const dirty = loaded
    ? !parsed?.ok || canonicalJson(parsed.value) !== canonicalJson(loaded.schema)
    : text.trim() !== ''
  const versionMismatch =
    loaded &&
    parsed?.ok &&
    (parsed.value.key !== loaded.key || parsed.value.version !== loaded.version)
      ? {
          json: `${String(parsed.value.key)} v${String(parsed.value.version)}`,
          loaded: `${loaded.key} v${loaded.version}`,
        }
      : null

  function adopt(form: ServerForm) {
    setLoaded(form)
    setText(pretty(form.schema))
    setServerResult(null)
  }

  const load = useMutation({
    mutationFn: ({ key, version }: { key: string; version: number }) =>
      getFormVersion(key, version),
    onSuccess: (form) => {
      adopt(form)
      setNotice(null)
    },
  })

  const save = useMutation({
    mutationFn: async () => {
      const body = (parsed as { ok: true; value: Record<string, unknown> }).value
      return loaded?.status === 'DRAFT'
        ? replaceFormDraft(loaded.key, loaded.version, body)
        : createFormDraft(body)
    },
    onSuccess: (form) => {
      adopt(form)
      void queryClient.invalidateQueries({ queryKey: formsListQuery.queryKey })
      setNotice(t('formsAdmin.saved', { key: form.key, version: form.version }))
    },
  })

  const publish = useMutation({
    mutationFn: async () =>
      publishFormVersion((loaded as ServerForm).key, (loaded as ServerForm).version),
    onSuccess: (form) => {
      adopt(form)
      void queryClient.invalidateQueries({ queryKey: formsListQuery.queryKey })
      setNotice(t('formsAdmin.published', { key: form.key, version: form.version }))
    },
  })

  const evaluate = useMutation({
    mutationFn: async () => {
      // Unveränderte, gespeicherte Version: per Schlüssel (jede Organisation); sonst die Definition (form.manage)
      const source =
        loaded && !dirty
          ? { key: loaded.key, version: loaded.version }
          : { definition: (parsed as { ok: true; value: Record<string, unknown> }).value }
      return evaluateOnServer(source, answers)
    },
    onSuccess: setServerResult,
  })

  const canSave =
    Boolean(parsed?.ok && schemaCheck?.valid) && !readOnly && !versionMismatch && dirty
  const canPublish = loaded?.status === 'DRAFT' && !dirty && Boolean(schemaCheck?.valid)
  const drift = serverResult && localResult ? evaluationDrift(localResult, serverResult) : null

  function startNewVersion() {
    if (!parsed?.ok) return
    const next = { ...parsed.value, version: Number(parsed.value.version) + 1 }
    setLoaded(null)
    setText(pretty(next))
    setServerResult(null)
    setNotice(null)
  }

  function submitLoad(key: string, version: number) {
    if (key && Number.isInteger(version) && version >= 1) load.mutate({ key, version })
  }

  const errorProblem = [load, save, publish, evaluate]
    .map((m) => (m.isError ? m : null))
    .find(Boolean)

  return (
    <>
      <PageHeader title={t('formsAdmin.title')} description={t('formsAdmin.description')}>
        <Button asChild variant="outline">
          <Link to="/admin/forms/preview">{t('formsAdmin.referencePreview')}</Link>
        </Button>
      </PageHeader>

      <section
        className="mb-6 grid gap-4 rounded-lg border bg-card p-5"
        aria-labelledby="forms-load"
      >
        <h2 id="forms-load" className="text-lg font-medium">
          {t('formsAdmin.loadTitle')}
        </h2>
        <form
          className="flex flex-wrap items-end gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            submitLoad(keyInput.trim(), Number(versionInput))
          }}
        >
          <div className="grid w-56 gap-1.5">
            <Label htmlFor={ids.key}>{t('formsAdmin.keyLabel')}</Label>
            <Combobox
              id={ids.key}
              value={keyInput}
              onChange={(key) => {
                setKeyInput(key)
                const newest = known.filter((f) => f.key === key).map((f) => f.version)
                setVersionInput(String(Math.max(...newest, 1)))
              }}
              options={[...new Set(known.map((f) => f.key))].map((key) => ({
                value: key,
                label: key,
              }))}
            />
          </div>
          <div className="grid w-32 gap-1.5">
            <Label htmlFor={ids.version}>{t('formsAdmin.versionLabel')}</Label>
            <Combobox
              id={ids.version}
              value={versionInput}
              onChange={setVersionInput}
              options={known
                .filter((f) => f.key === keyInput)
                .map((f) => f.version)
                .sort((x, y) => y - x)
                .map((v) => ({ value: String(v), label: `v${v}` }))}
            />
          </div>
          <Button type="submit" disabled={load.isPending}>
            {t('formsAdmin.load')}
          </Button>
        </form>
        <div>
          <p className="mb-1 text-sm font-medium">{t('formsAdmin.known')}</p>
          {forms.data ? (
            <div className="overflow-x-auto rounded-md border">
              <table className="w-full text-left text-sm" aria-label={t('formsAdmin.known')}>
                <thead className="bg-muted">
                  <tr>
                    <th scope="col" className="px-3 py-2">
                      {t('formsAdmin.colForm')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('formsAdmin.colStatus')}
                    </th>
                    <th scope="col" className="px-3 py-2">
                      {t('formsAdmin.colPublished')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {forms.data.map((entry) => (
                    <tr key={`${entry.key}:${entry.version}`} className="border-t">
                      <td className="px-3 py-1">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setKeyInput(entry.key)
                            setVersionInput(String(entry.version))
                            submitLoad(entry.key, entry.version)
                          }}
                        >
                          {entry.key} v{entry.version}
                        </Button>
                        <span className="ml-2 text-muted-foreground">
                          {pickText(entry.title, lang)}
                        </span>
                      </td>
                      <td className="px-3 py-1">
                        <Badge variant={entry.status === 'PUBLISHED' ? 'default' : 'secondary'}>
                          {t(`formsAdmin.statusValue.${entry.status}`)}
                        </Badge>
                      </td>
                      <td className="px-3 py-1">
                        {entry.publishedAt
                          ? new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(
                              new Date(entry.publishedAt),
                            )
                          : t('common.none')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : forms.isPending ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : (
            <>
              <p className="mb-2 text-sm text-muted-foreground">{t('errors.notConnectedBody')}</p>
              <ul className="flex flex-wrap gap-2">
                {FALLBACK.map((entry) => (
                  <li key={`${entry.key}:${entry.version}`}>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setKeyInput(entry.key)
                        setVersionInput(String(entry.version))
                        submitLoad(entry.key, entry.version)
                      }}
                    >
                      {entry.key} v{entry.version}
                    </Button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </section>

      <div className="mb-4 flex flex-wrap items-center gap-3" aria-live="polite">
        {loaded ? (
          <>
            <span className="font-medium">
              {t('formsAdmin.current', { key: loaded.key, version: loaded.version })}
            </span>
            <Badge
              variant={loaded.status === 'PUBLISHED' ? 'default' : 'secondary'}
              data-testid="form-status"
            >
              {t(`formsAdmin.statusValue.${loaded.status}`)}
            </Badge>
          </>
        ) : (
          <span className="text-muted-foreground">{t('formsAdmin.notLoaded')}</span>
        )}
        {notice && (
          <span role="status" className="font-medium">
            {notice}
          </span>
        )}
      </div>

      {errorProblem && <ProblemBox error={errorProblem.error} />}

      <div className="grid items-start gap-6 xl:grid-cols-2">
        <section className="grid gap-3">
          <Label htmlFor={ids.editor} className="text-base">
            {t('formsAdmin.editorLabel')}
          </Label>
          {readOnly && (
            <p className="text-sm text-muted-foreground">{t('formsAdmin.readOnlyNote')}</p>
          )}
          <Textarea
            id={ids.editor}
            value={text}
            readOnly={readOnly}
            spellCheck={false}
            rows={24}
            className="font-mono text-sm"
            aria-invalid={parsed ? !parsed.ok || schemaCheck?.valid === false : undefined}
            aria-describedby={ids.summary}
            onChange={(event) => setText(event.target.value)}
          />

          <div id={ids.summary} className="text-sm" data-testid="schema-summary">
            {parsed === null ? null : !parsed.ok ? (
              <p role="alert" className="text-destructive">
                {t('formsAdmin.jsonError', { message: parsed.message })}
              </p>
            ) : schemaCheck?.valid ? (
              <p className="font-medium">{t('formsAdmin.schemaOk')}</p>
            ) : (
              <div role="alert" className="text-destructive">
                <p className="font-medium">
                  {t('formsAdmin.schemaErrors', { count: schemaCheck?.errors.length ?? 0 })}
                </p>
                <ul className="list-disc pl-5">
                  {schemaCheck?.errors.slice(0, 12).map((violation) => (
                    <li key={`${violation.path}-${violation.message}`}>
                      <code>{violation.path}</code> {violation.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {versionMismatch && (
              <p role="alert" className="mt-1 text-destructive">
                {t('formsAdmin.versionMismatch', versionMismatch)}
              </p>
            )}
            {loaded?.status === 'DRAFT' && dirty && (
              <p className="mt-1">{t('formsAdmin.unsavedChanges')}</p>
            )}
          </div>

          <div role="toolbar" aria-label={t('formsAdmin.actions')} className="flex flex-wrap gap-3">
            {readOnly && (
              <Button type="button" variant="outline" onClick={startNewVersion}>
                {t('formsAdmin.newVersion')}
              </Button>
            )}
            {!readOnly && (
              <Button
                type="button"
                disabled={!canSave || save.isPending}
                onClick={() => save.mutate()}
              >
                {t('formsAdmin.saveDraft')}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              disabled={!definition || evaluate.isPending}
              onClick={() => evaluate.mutate()}
            >
              {evaluate.isPending ? t('formsAdmin.evaluating') : t('formsAdmin.evaluate')}
            </Button>
            {loaded?.status === 'DRAFT' && (
              <Button
                type="button"
                variant="destructive"
                disabled={!canPublish}
                onClick={() => setPublishOpen(true)}
              >
                {t('formsAdmin.publish')}
              </Button>
            )}
          </div>
        </section>

        <section className="grid gap-4" aria-labelledby="forms-preview">
          <h2 id="forms-preview" className="text-lg font-medium">
            {t('formsAdmin.preview')}
          </h2>
          {definition ? (
            <>
              <div className="rounded-lg border bg-card p-5" data-testid="form-preview">
                <h3 className="mb-1 text-xl font-semibold">{pickText(definition.title, lang)}</h3>
                <FormRenderer
                  key={loaded ? `${loaded.key}:${loaded.version}` : 'new'}
                  definition={definition}
                  onEvaluate={setLocalResult}
                  onAnswers={setAnswers}
                />
              </div>
              {localResult && (
                <ResultPanel
                  definition={definition}
                  result={localResult}
                  title={t('forms.resultArea')}
                  testId="local-result"
                  headingLevel={3}
                />
              )}
              {serverResult && (
                <>
                  <ResultPanel
                    definition={definition}
                    result={{ ...serverResult, outcome: serverResult.outcome ?? null }}
                    title={t('formsAdmin.serverResult')}
                    testId="server-result"
                    headingLevel={3}
                  />
                  {drift && (
                    <p
                      role={drift.length > 0 ? 'alert' : 'status'}
                      data-testid="drift"
                      className={drift.length > 0 ? 'font-medium text-destructive' : 'font-medium'}
                    >
                      {drift.length > 0
                        ? t('formsAdmin.drift', { fields: drift.join(', ') })
                        : t('formsAdmin.noDrift')}
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">{t('formsAdmin.previewHint')}</p>
          )}
        </section>
      </div>

      {publishOpen && loaded && (
        <ConfirmActionDialog
          open
          onOpenChange={(open) => !open && setPublishOpen(false)}
          actionLabel={t('formsAdmin.publish')}
          description={t('formsAdmin.publishDescription')}
          irreversible
          // Die Veröffentlichung kennt keinen Begründungstext; nichts abfragen, das nirgends gespeichert wird.
          requireReason={false}
          onConfirm={async () => {
            await publish.mutateAsync()
          }}
        />
      )}
    </>
  )
}

function ProblemBox({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const { title, items } = describeProblem(error)
  return (
    <div
      role="alert"
      className="mb-4 rounded-md border border-destructive p-3 text-sm text-destructive"
      data-testid="problem"
    >
      <p className="font-medium">{title || t('formsAdmin.loadFailed')}</p>
      {items.length > 0 && (
        <>
          <p className="mt-1">{t('formsAdmin.violations')}:</p>
          <ul className="list-disc pl-5">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
